import type { StateCreator } from 'zustand';

import type { QualityFormulaPreviewResult, QualityFormulaPreviewSummary } from '../../qualityFormulaTypes';
import type { QualityFormulaStore } from '../qualityFormulaStore';

import { calculateQualityAdjustments } from '../../../../api/pricingEngineApi';
import {
  showErrorNotification,
  showSuccessNotification,
  showValidationErrorNotification,
  showWarningNotification,
} from '../../../../utils/notifications';
import { pluralize } from '../../pluralize';
import {
  buildCalculateAdjustmentsRequest,
  describeCalculateAdjustmentsValidationError,
} from '../../qualityFormulaRequest';
import { mapCalculateAdjustmentsResponseToPreviewResults } from '../../qualityFormulaResponseMapping';
import { validateEnabledSpecStates } from '../../qualityFormulaValidation';

export interface RunPreviewSlice {
  /** Clears Step 3's results/summary — used both by a Step 1 deselection (see `specFormSlice`) and a full reset. */
  handleClearResults: () => void;
  isTesting: boolean;
  results: QualityFormulaPreviewResult[];
  runPreview: () => Promise<void>;
  summary: null | QualityFormulaPreviewSummary;
}

/**
 * Owns running the preview itself — validating Step 1/2's current state, calling the shared
 * Pricing Engine API (POST /quality-adjustments/calculate) instead of calculating locally so the
 * playground always reflects the same math as production, and the resulting Step 3
 * results/summary. Reads Step 1/2's state via `get()` (rather than taking it as an argument) now
 * that both live in the same store, so it never risks closing over a stale snapshot.
 */
export const createRunPreviewSlice: StateCreator<QualityFormulaStore, [], [], RunPreviewSlice> = (set, get) => ({
  handleClearResults: () => {
    set({ results: [], summary: null });
  },
  isTesting: false,
  results: [],

  runPreview: async () => {
    const { actualValues, deliveredQuantity, specStates, testComponentValues, testDeemedPriceOverrides, tradePrice } =
      get();
    const hasEnabledSpec = Object.values(specStates).some(state => state.enabled);
    if (!hasEnabledSpec) {
      showWarningNotification('No specs were selected. Select at least one quality spec to test.');
      return;
    }

    // Blocks the API call entirely when a required field is missing/invalid — surfacing that only
    // via the red-bordered inputs is easy to miss, especially inside a collapsed spec card.
    const missingFields = validateEnabledSpecStates(specStates);
    if (missingFields.length > 0) {
      get().expandSpecs(missingFields.map(field => field.specType));
      showValidationErrorNotification(
        'Cannot test formulas. Please complete all required fields.',
        missingFields.map(field => `${field.specLabel} → ${field.fieldLabel}: ${field.reason}`),
      );
      // `missingFields` is built by iterating SPEC_TYPES in the same order the cards render in,
      // so its first entry is always the earliest (topmost) card with a problem — with several
      // cards missing fields, only that one needs scrolling into view. Waiting out Mantine
      // Collapse's 200ms expand animation (rather than scrolling immediately) means the target
      // card has already grown to its full height, so it ends up properly centered instead of
      // scrolling to where its still-collapsed (short) height used to be.
      window.setTimeout(() => {
        document
          .getElementById(`spec-card-${missingFields[0].specType}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
      return;
    }

    set({ isTesting: true });
    try {
      const request = buildCalculateAdjustmentsRequest({
        actualValues,
        deliveredQuantity,
        specStates,
        testComponentValues,
        testDeemedPriceOverrides,
        tradePrice,
      });
      const response = await calculateQualityAdjustments(request);
      const { results, summary } = mapCalculateAdjustmentsResponseToPreviewResults({ response, specStates });

      set({ results, summary });
      showSuccessNotification(`${pluralize(results.length, 'formula preview')} ready.`);
    } catch (error) {
      // The shared Zod schema (imported from @local/lib, not hand-duplicated) is the single
      // source of truth for what makes a config valid — surface its rejections as a precise list
      // instead of the generic fallback below.
      const validationItems = describeCalculateAdjustmentsValidationError(error);
      if (validationItems) {
        showValidationErrorNotification('This configuration is not valid.', validationItems);
      } else {
        showErrorNotification(error, 'Unable to calculate quality adjustments');
      }
    } finally {
      set({ isTesting: false });
    }
  },

  summary: null,
});
